import React, { createContext, useReducer, useEffect } from 'react';
import { notification } from 'antd';
import { authService } from '../services';
import { jwtDecode } from '../utils/jwtHelpers';
import { AUTH_STORAGE_KEY } from '../constants';
import { AuthState, AuthAction, AuthContextType, LoginCredentials } from '../types';
import { permissionService } from '../../../services/permissionService';
import { identifyUser, resetIdentity } from '../../../utils/analytics';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-auth');

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: true,
  error: null,
  atomicPermissions: new Set<string>(),
  isSuperPermission: false,
  // Start true: until the first /me/permissions resolves, permission checks must
  // wait rather than treat the empty Set as "denied".
  atomicPermissionsLoading: true
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_INIT':
      return { ...state, loading: true };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null
      };
    case 'ATOMIC_PERMISSIONS_LOADING':
      return { ...state, atomicPermissionsLoading: true };
    case 'ATOMIC_PERMISSIONS_LOADED':
      return {
        ...state,
        atomicPermissions: action.payload.atomicPermissions,
        isSuperPermission: action.payload.isSuper,
        atomicPermissionsLoading: false
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
        atomicPermissions: new Set<string>(),
        isSuperPermission: false,
        atomicPermissionsLoading: false
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: null,
        atomicPermissions: new Set<string>(),
        isSuperPermission: false,
        atomicPermissionsLoading: false
      };
    default:
      return state;
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  /**
   * Load the user's GLOBAL atomic permissions (CASL) from /permissions/me/permissions.
   *
   * Builds a Set of atomic permission strings plus an `isSuper` flag (wildcard
   * subject "all"). This drives the `can()` helper.
   */
  const loadAtomicPermissions = async (): Promise<void> => {
    try {
      dispatch({ type: 'ATOMIC_PERMISSIONS_LOADING' });
      const { permissions } = await permissionService.getMyPermissions();
      const isSuper = permissions.some((p) => p.subject === 'all');
      const atomicPermissions = new Set(permissions.map((p) => p.subject));

      dispatch({
        type: 'ATOMIC_PERMISSIONS_LOADED',
        payload: { atomicPermissions, isSuper }
      });
    } catch (error) {
      logger.error('Failed to load atomic permissions');
      // Fail closed: no atomic permissions cached (can() returns false).
      dispatch({
        type: 'ATOMIC_PERMISSIONS_LOADED',
        payload: { atomicPermissions: new Set<string>(), isSuper: false }
      });
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      dispatch({ type: 'AUTH_INIT' });

      // No need to load resources separately anymore - they come with permissions!

      const token = localStorage.getItem(AUTH_STORAGE_KEY);

      if (token) {
        try {
          // const isValid = await authService.validateToken(token);
          // TODO: Use authService.validateToken(token) when endpoints are available in dev stage
          const isValid = true;

          if (isValid) {
            const userData = jwtDecode(token);

            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user: userData,
                token
              }
            });

            // Restored session: identify too, not just on fresh login. A user
            // who reloads (or whose PostHog cookie was cleared) would otherwise
            // stay anonymous for the rest of the session.
            identifyUser(userData);

            // Load the user's atomic permissions after restoring the session.
            await loadAtomicPermissions();

            // Clear all persistent notifications when session is restored
            notification.destroy();
          } else {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            dispatch({ type: 'AUTH_ERROR', payload: 'Sesión expirada' });
          }
        } catch (error) {
          localStorage.removeItem(AUTH_STORAGE_KEY);
          dispatch({ type: 'AUTH_ERROR', payload: error.message });
        }
      } else {
        dispatch({ type: 'AUTH_ERROR', payload: null });
      }
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'AUTH_INIT' });
      const response = await authService.login(credentials);
      const { accessToken, refreshToken, user } = response;

      localStorage.setItem(AUTH_STORAGE_KEY, accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token: accessToken }
      });

      // Bind the PostHog person to this user + account group (IMP-311).
      identifyUser(user);

      // Load the user's atomic permissions after login.
      await loadAtomicPermissions();

      // Clear all persistent notifications on successful login
      notification.destroy();

      return true;
    } catch (error) {
      dispatch({ type: 'AUTH_ERROR', payload: error.message });
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem('refreshToken');
    // Drop the PostHog identity so a shared browser does not attribute the next
    // session to the person who just left (IMP-311).
    resetIdentity();
    dispatch({ type: 'LOGOUT' });
  };

  /**
   * Check if the user has an atomic permission (CASL).
   *
   * Synchronous, in-memory lookup against the Set loaded at login. Match the
   * EXACT string the backend checks (e.g. "agent.process_flows.view").
   * Superadmin/superuser (wildcard "all") always return true.
   *
   * @param permission - Full atomic permission string from the catalog.
   * @returns True if the user has the permission.
   */
  const can = (permission: string): boolean => {
    return state.isSuperPermission || state.atomicPermissions.has(permission);
  };

  const value = {
    ...state,
    login,
    logout,
    can
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 